'use client';

import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import { toExternalHref } from '@up2stay/up2promo-commons';

import { cn } from '../../utils/cn';
import { useProfileLayout } from '../layouts/profile-layout-context';
import { getSocialPlatformLabel, SocialLinkIcon } from '../lib/social-icons';
import {
  ModuleEntryCard,
  ModuleEntryIcon,
  ModuleEntryLabelValue,
  ModuleEntryRow,
} from './module-entry-card';

interface SocialsModuleDisplayProps {
  module: ProfileModuleDto;
}

type SocialPlatform =
  | 'instagram'
  | 'linkedin'
  | 'twitter'
  | 'x'
  | 'facebook'
  | 'tiktok'
  | 'youtube'
  | 'github'
  | 'behance'
  | 'dribbble'
  | 'pinterest'
  | 'snapchat'
  | 'twitch'
  | 'custom';

type SocialLink = {
  platform: SocialPlatform;
  url: string;
  label?: string;
  icon?: string;
  visible?: boolean;
};

type SocialsModuleConfig = {
  type: 'socials';
  links: SocialLink[];
  layout?: 'grid' | 'list' | 'minimal';
};

function getSocialsConfig(
  rawConfig: Record<string, unknown>,
): SocialsModuleConfig {
  const layout = rawConfig.layout;
  const resolvedLayout =
    layout === 'list' ? 'list' : layout === 'minimal' ? 'minimal' : 'grid';
  return {
    type: 'socials',
    links: (rawConfig.links as SocialLink[]) || [],
    layout: resolvedLayout,
  };
}

/**
 * Displays social media links with platform icons.
 */
export function SocialsModuleDisplay({ module }: SocialsModuleDisplayProps) {
  const profileLayout = useProfileLayout();
  const config = getSocialsConfig(module.config || {});

  const visibleLinks = (config.links || []).filter(
    (link) => link.visible !== false && link.url,
  );

  if (visibleLinks.length === 0) {
    return null;
  }

  const linkLabel = (link: SocialLink) =>
    link.label?.trim() || getSocialPlatformLabel(link.platform);

  if (profileLayout === 'centered') {
    // Handover hero socials sit 22px under bio; as a module (after other
    // sections) use the same 30px rhythm as centered seclabels.
    return (
      <div
        className={cn(
          'mt-[30px] flex w-full flex-wrap items-center justify-center',
          visibleLinks.length > 2 ? 'gap-3.5' : 'gap-3',
        )}
      >
        {visibleLinks.map((link, index) => (
          <a
            key={index}
            href={toExternalHref(link.url)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={linkLabel(link)}
            className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surface)] text-[var(--text)] transition-colors hover:border-[var(--muted)] hover:bg-[var(--surface2)]"
          >
            <SocialLinkIcon
              link={link}
              className="h-[22px] w-[22px] shrink-0"
            />
          </a>
        ))}
      </div>
    );
  }

  if (profileLayout === 'classic') {
    // Handover Elsewhere pills: 9px gap, 14px below seclabel on mobile.
    return (
      <div className="mt-3.5 flex flex-wrap gap-[9px] @min-[880px]/profile:mt-0">
        {visibleLinks.map((link, index) => (
          <a
            key={index}
            href={toExternalHref(link.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border-[1.5px] border-[var(--border)] px-[15px] py-2.5 text-[13px] font-medium text-[var(--text)]"
          >
            <SocialLinkIcon link={link} className="h-[15px] w-[15px]" />
            {linkLabel(link)}
          </a>
        ))}
      </div>
    );
  }

  const layout = config.layout || 'grid';

  if (layout === 'minimal') {
    return (
      <div className="flex w-full flex-wrap items-center gap-3">
        {visibleLinks.map((link, index) => (
          <ModuleEntryCard key={index} className="w-auto">
            <a
              href={toExternalHref(link.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="block transition-colors"
              aria-label={linkLabel(link)}
            >
              <ModuleEntryIcon className="h-12 w-12 [&_svg]:h-6 [&_svg]:w-6">
                <SocialLinkIcon link={link} />
              </ModuleEntryIcon>
            </a>
          </ModuleEntryCard>
        ))}
      </div>
    );
  }

  if (layout === 'list') {
    return (
      <div className="w-full space-y-2.5">
        {visibleLinks.map((link, index) => (
          <ModuleEntryCard key={index}>
            <a
              href={toExternalHref(link.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="block transition-colors"
              aria-label={linkLabel(link)}
            >
              <ModuleEntryRow>
                <ModuleEntryIcon>
                  <SocialLinkIcon link={link} />
                </ModuleEntryIcon>
                <ModuleEntryLabelValue value={linkLabel(link)} />
              </ModuleEntryRow>
            </a>
          </ModuleEntryCard>
        ))}
      </div>
    );
  }

  return (
    <div className="grid w-full grid-cols-2 gap-2.5">
      {visibleLinks.map((link, index) => (
        <a
          key={index}
          href={toExternalHref(link.url)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={linkLabel(link)}
          className={cn(
            'flex items-center gap-[11px] rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-3.5 text-[13.5px] font-semibold text-[var(--text)]',
          )}
        >
          <span className="[&_svg]:h-[22px] [&_svg]:w-[22px]">
            <SocialLinkIcon link={link} />
          </span>
          <span className="truncate">{linkLabel(link)}</span>
        </a>
      ))}
    </div>
  );
}
